/**
 * SkinStorage — durable persistence for SkinResolver's two large maps:
 *   - skins:    user-imported sprite/audio overrides (data URLs)
 *   - tilesets: imported source atlas images (data URLs)
 *
 * Why this exists: a single uploaded MP3 or PNG is typically 100 KB+
 * when re-encoded as a base64 data URL, and the editor's Content
 * Browser exposes dozens of slots. The legacy localStorage backend
 * had a ~5 MB-per-origin quota — once a designer imported a few
 * audio clips and tilesets, the next `setItem` threw
 * `QuotaExceededError`, the error was logged-only (invisible to the
 * user), and the next reload silently restored the LAST successful
 * save instead of the new import. Result: "I always have to re-add
 * music after each reload."
 *
 * IndexedDB has a quota measured in hundreds of MB / multiple GB on
 * every modern browser, so the same data fits with room to spare.
 * The API is async (which leaks into SkinResolver's `ready()`
 * contract), but the load only happens once at boot and the write
 * runs in the background, so the UX cost is essentially zero.
 *
 * One-time migration: when IDB is empty on first run but the legacy
 * localStorage keys still hold data, we copy them into IDB and then
 * clear the localStorage entries to free that quota for other things
 * (settings, language, player name still live in localStorage —
 * they're small).
 *
 * Fallback: if IndexedDB is unavailable (private-mode Safari, certain
 * embedded WebViews, very old browsers), we fall through to
 * localStorage so the editor still works for small projects. The
 * caller is told via the return value of `saveSkins/saveTilesets`
 * whether a write actually landed, so the UI can surface a clear
 * "storage full" error when it doesn't.
 */

const DB_NAME = 'mini-games-skin-db';
const DB_VERSION = 1;
const STORE_SKINS = 'skins';
const STORE_TILESETS = 'tilesets';
const KEY_ALL = 'all';

const LS_SKINS = 'mini-games-skins';
const LS_TILESETS = 'mini-games-tilesets';

export class SkinStorage {
    /** @type {IDBDatabase|null} */
    _db = null;
    /** @type {Promise<IDBDatabase|null>} */
    _ready = null;

    constructor() {
        this._ready = this._open().catch((err) => {
            console.warn(
                '[SkinStorage] IndexedDB unavailable, falling back to localStorage:',
                err?.message || err
            );
            this._db = null;
            return null;
        });
    }

    /**
     * Wait for the IDB connection (or fallback) to be ready.
     * Resolves to `true` when IDB is usable, `false` when only
     * localStorage is available.
     */
    async ready() {
        await this._ready;
        return !!this._db;
    }

    /**
     * Hydrate skins + tilesets. The very first time this runs on a
     * device that previously persisted via localStorage, we migrate
     * those keys into IDB so the user keeps every import they made
     * before the storage backend changed.
     *
     * @returns {Promise<{skins:object, tilesets:object}>}
     */
    async load() {
        await this._ready;
        const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

        let skins = null;
        let tilesets = null;

        if (this._db) {
            skins    = await this._idbGet(STORE_SKINS);
            tilesets = await this._idbGet(STORE_TILESETS);

            // One-time migration from legacy localStorage entries.
            const lsSkins    = this._lsGet(LS_SKINS);
            const lsTilesets = this._lsGet(LS_TILESETS);
            let migrated = false;
            if (!isObj(skins) && isObj(lsSkins)) {
                skins = lsSkins;
                await this._idbSet(STORE_SKINS, skins);
                migrated = true;
            }
            if (!isObj(tilesets) && isObj(lsTilesets)) {
                tilesets = lsTilesets;
                await this._idbSet(STORE_TILESETS, tilesets);
                migrated = true;
            }
            if (migrated) {
                // Free the legacy quota — IDB is now authoritative.
                try { localStorage.removeItem(LS_SKINS); } catch (_) {}
                try { localStorage.removeItem(LS_TILESETS); } catch (_) {}
                console.log(
                    '[SkinStorage] Migrated skin/tileset data from localStorage to IndexedDB.'
                );
            }
        } else {
            // No IDB at all — read straight from localStorage so old
            // installs still work, just with the small quota.
            skins    = this._lsGet(LS_SKINS);
            tilesets = this._lsGet(LS_TILESETS);
        }

        return {
            skins:    isObj(skins)    ? skins    : {},
            tilesets: isObj(tilesets) ? tilesets : {},
        };
    }

    /**
     * Persist the full skins map. Returns `true` when the write
     * actually landed (IDB or LS fallback), `false` otherwise.
     */
    async saveSkins(skins) {
        return this._save(STORE_SKINS, LS_SKINS, skins);
    }

    /** Persist the full tilesets map. Same return contract as `saveSkins`. */
    async saveTilesets(tilesets) {
        return this._save(STORE_TILESETS, LS_TILESETS, tilesets);
    }

    /** Wipe both stores (also removes any legacy localStorage entries). */
    async clear() {
        await this._ready;
        if (this._db) {
            await this._idbDelete(STORE_SKINS);
            await this._idbDelete(STORE_TILESETS);
        }
        try { localStorage.removeItem(LS_SKINS);    } catch (_) {}
        try { localStorage.removeItem(LS_TILESETS); } catch (_) {}
    }

    // ── internals ────────────────────────────────────────────────────

    _open() {
        return new Promise((resolve, reject) => {
            if (typeof indexedDB === 'undefined') {
                return reject(new Error('IndexedDB API not present'));
            }
            const req = indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE_SKINS))    db.createObjectStore(STORE_SKINS);
                if (!db.objectStoreNames.contains(STORE_TILESETS)) db.createObjectStore(STORE_TILESETS);
            };
            req.onsuccess = () => { this._db = req.result; resolve(req.result); };
            req.onerror   = () => reject(req.error || new Error('IDB open failed'));
            req.onblocked = () => reject(new Error('IDB open blocked (another tab is upgrading)'));
        });
    }

    async _save(store, lsKey, value) {
        await this._ready;
        if (this._db) {
            const ok = await this._idbSet(store, value);
            if (ok) return true;
            // IDB write failed (very rare — usually a per-origin quota
            // policy). Try localStorage as a last-chance fallback so a
            // small project at least keeps working.
        }
        return this._lsSet(lsKey, value);
    }

    _idbGet(store) {
        if (!this._db) return Promise.resolve(null);
        return new Promise((resolve) => {
            try {
                const tx = this._db.transaction([store], 'readonly');
                const req = tx.objectStore(store).get(KEY_ALL);
                req.onsuccess = () => resolve(req.result ?? null);
                req.onerror   = () => resolve(null);
            } catch (_) { resolve(null); }
        });
    }

    _idbSet(store, value) {
        if (!this._db) return Promise.resolve(false);
        return new Promise((resolve) => {
            try {
                const tx = this._db.transaction([store], 'readwrite');
                tx.objectStore(store).put(value, KEY_ALL);
                tx.oncomplete = () => resolve(true);
                tx.onerror    = () => resolve(false);
                tx.onabort    = () => resolve(false);
            } catch (err) {
                console.warn('[SkinStorage] IDB write failed:', err?.message || err);
                resolve(false);
            }
        });
    }

    _idbDelete(store) {
        if (!this._db) return Promise.resolve(false);
        return new Promise((resolve) => {
            try {
                const tx = this._db.transaction([store], 'readwrite');
                tx.objectStore(store).delete(KEY_ALL);
                tx.oncomplete = () => resolve(true);
                tx.onerror    = () => resolve(false);
            } catch (_) { resolve(false); }
        });
    }

    _lsGet(key) {
        try {
            const raw = localStorage.getItem(key);
            return raw ? JSON.parse(raw) : null;
        } catch (_) { return null; }
    }

    _lsSet(key, value) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (err) {
            console.warn('[SkinStorage] localStorage save failed:', err?.message || err);
            return false;
        }
    }
}
